import React, { useState, useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { BiArrowToLeft, BiCheck, BiChevronRight } from 'react-icons/bi'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import paymentService from '../services/paymentService'
import agencyService from '../services/agencyService'
import { config } from '../config'

// Initialize Stripe
const stripePromise = loadStripe(config.stripe.publicKey)

interface AgencyFormData {
  name: string
  agencyCode: string
  phone: string
  website: string
  description: string
}

interface SubscriptionPlan {
  id: string
  name: string
  price: number
  description: string
  features: string[]
  stripePriceId: string
}

interface BillingInfo {
  companyName: string
  billingEmail: string
  address: string
  city: string
  postalCode: string
  country: string
  vatNumber?: string
}

interface FormDataForRedirect {
  agencyData: AgencyFormData
  selectedPlan: SubscriptionPlan
  billingInfo: BillingInfo
  subscriptionId?: string
  customerId?: string
}

// Default subscription plan structure - price will be set dynamically
const createSubscriptionPlan = (price: number): SubscriptionPlan => ({
  id: 'standard',
  name: 'Agency Standard',
  price,
  description: 'Perfect for growing agencies',
  features: [
    'Advanced analytics',
    'Priority support',
    'Monthly reporting'
  ],
  stripePriceId: '' // Will be set dynamically when subscription is created
})

// Helper functions for URL parameter encoding/decoding
const encodeFormData = (data: FormDataForRedirect): string => {
  try {
    return btoa(encodeURIComponent(JSON.stringify(data)))
  } catch (error) {
    console.error('❌ Error encoding form data:', error)
    return ''
  }
}

const decodeFormData = (encoded: string): FormDataForRedirect | null => {
  try {
    return JSON.parse(decodeURIComponent(atob(encoded)))
  } catch (error) {
    console.error('❌ Error decoding form data:', error)
    return null
  }
}

const CreateAgencyWizard: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [currentStep, setCurrentStep] = useState(1)
  const [existingAgencyId, setExistingAgencyId] = useState<string>('')
  const [isExistingAgency, setIsExistingAgency] = useState(false)
  // Note: User authentication is handled by the auth system automatically

  // Note: We no longer load system settings since agency subscriptions should use agency-specific pricing
  // The price will be set when the agency data is loaded in loadExistingAgencyData

  // Initialize step and agency data from URL parameters
  useEffect(() => {
    const step = searchParams.get('step')
    const agencyId = searchParams.get('agency')

    if (agencyId) {
      setExistingAgencyId(agencyId)
      setIsExistingAgency(true)
      setCurrentAgencyId(agencyId)
      setCreatedAgencyId(agencyId)

      // Load existing agency data
      loadExistingAgencyData(agencyId)
    }

    if (step) {
      const stepNumber = parseInt(step, 10)
      if (stepNumber >= 1 && stepNumber <= 4) {
        // Control step access based on agency setup
        if (stepNumber === 1 && agencyId) {
          // Can't go to step 1 if agency already exists, redirect to step 2
          setCurrentStep(2)
          navigate(`/agencies/create?step=2&agency=${agencyId}`, { replace: true })
        } else if (stepNumber === 2 && !agencyId) {
          // Can't go to step 2 without agency, redirect to step 1
          setCurrentStep(1)
          navigate('/agencies/create?step=1', { replace: true })
        } else if ((stepNumber === 3 || stepNumber === 4) && !agencyId) {
          // Can't go to steps 3 or 4 without going through step 2
          setCurrentStep(1)
          navigate('/agencies/create?step=1', { replace: true })
        } else {
          setCurrentStep(stepNumber)
        }
      }
    } else if (agencyId) {
      // If agency ID is provided but no step, default to step 2 (subscription)
      setCurrentStep(2)
      navigate(`/agencies/create?step=2&agency=${agencyId}`, { replace: true })
    }
  }, [searchParams])

  // Load existing agency data
  const loadExistingAgencyData = async (agencyId: string) => {
    try {
      const agencyData = await agencyService.getAgencyById(agencyId)
      setAgencyData({
        name: agencyData.name,
        agencyCode: agencyData.agencyCode,
        phone: agencyData.phone || '',
        website: agencyData.website || '',
        description: agencyData.description || '',

      })

      if (agencyData.billingInfo) {
        setBillingInfo({
          companyName: agencyData.billingInfo.companyName || '',
          billingEmail: agencyData.billingInfo.billingEmail || '',
          address: agencyData.billingInfo.address || '',
          city: agencyData.billingInfo.city || '',
          postalCode: agencyData.billingInfo.postalCode || '',
          country: agencyData.billingInfo.country || 'GB',
          vatNumber: agencyData.billingInfo.vatNumber || ''
        })
      }

      // Update subscription plan price based on agency settings if available
      if (agencyData.settings?.defaultAgencyBasePlanCost) {
        const priceInPounds = agencyData.settings.defaultAgencyBasePlanCost / 100
        const updatedPlan = createSubscriptionPlan(priceInPounds)
        setSelectedPlan(updatedPlan)
      } else {
        // For new agencies or agencies without custom pricing, use a default price
        // This will be updated when the agency is created with proper settings
        const defaultPrice = 44.99 // Default price in pounds
        const updatedPlan = createSubscriptionPlan(defaultPrice)
        setSelectedPlan(updatedPlan)
      }
    } catch (error) {
      console.error('❌ Error loading existing agency data:', error)
    }
  }

  // Log step changes
  useEffect(() => {
    console.log('📍 Current step changed to:', currentStep)
  }, [currentStep])

  // Form data states
  const [agencyData, setAgencyData] = useState<AgencyFormData>({
    name: '',
    agencyCode: '',
    phone: '',
    website: '',
    description: ''
  })

  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan>(createSubscriptionPlan(44.99))

  const [billingInfo, setBillingInfo] = useState<BillingInfo>({
    companyName: '',
    billingEmail: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'GB',
    vatNumber: ''
  })


  const [clientSecret, setClientSecret] = useState<string>('')
  const [subscriptionId, setSubscriptionId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingPaymentStatus, setIsCheckingPaymentStatus] = useState(false)
  const [createdAgencyId, setCreatedAgencyId] = useState<string>('')
  const [currentAgencyId, setCurrentAgencyId] = useState<string>('')
  const [paymentError, setPaymentError] = useState<string>('')
  const [confirmedPaymentIntents, setConfirmedPaymentIntents] = useState<Set<string>>(new Set())

  // Log when payment step is rendered (moved after state declarations)
  useEffect(() => {
    if (currentStep === 3 && clientSecret) {
      console.log('🎯 Rendering PaymentInformationStep - currentStep:', currentStep, 'clientSecret:', !!clientSecret)
    }
  }, [currentStep, clientSecret])

  // Check if returning from payment redirect
  useEffect(() => {
    const step = searchParams.get('step')
    const paymentIntent = searchParams.get('payment_intent')
    const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret')
    const encodedFormData = searchParams.get('data')

    const checkPaymentStatus = async () => {
      if (paymentIntent && paymentIntentClientSecret) {
        console.log('🔍 Checking payment status after redirect...')
        setIsCheckingPaymentStatus(true)
        try {
          const stripe = await stripePromise
          if (stripe) {
            const { paymentIntent: retrievedPaymentIntent } = await stripe.retrievePaymentIntent(paymentIntentClientSecret)
            console.log('📋 Retrieved payment intent:', retrievedPaymentIntent)

            if (retrievedPaymentIntent?.status === 'succeeded') {
              console.log('✅ Payment succeeded! Creating agency after redirect...')

              // Retrieve form data from URL parameters
              if (encodedFormData) {
                try {
                  const savedFormData = decodeFormData(encodedFormData)
                  if (savedFormData) {
                    console.log('📥 Retrieved saved form data from URL:', savedFormData)

                    // Update state with saved data
                    setAgencyData(savedFormData.agencyData)
                    setSelectedPlan(savedFormData.selectedPlan)
                    setBillingInfo(savedFormData.billingInfo)

                    console.log('🔍 Saved form data debugging:', {
                      'savedFormData.selectedPlan.stripePriceId': savedFormData.selectedPlan.stripePriceId,
                      'savedFormData.selectedPlan': savedFormData.selectedPlan,
                      'savedFormData.customerId': savedFormData.customerId,
                      'savedFormData.subscriptionId': savedFormData.subscriptionId
                    })

                    // Get the current agency ID from URL if available
                    const savedAgencyId = searchParams.get('agency')
                    if (savedAgencyId) {
                      setCurrentAgencyId(savedAgencyId)
                      setCreatedAgencyId(savedAgencyId)
                      console.log('📍 Found saved agency ID:', savedAgencyId)
                    }

                    // Update agency after successful payment redirect with retrieved data
                    try {
                      const customerId = savedFormData.customerId
                      const subscriptionId = savedFormData.subscriptionId

                      console.log('🔍 Using saved data - customerId:', customerId, 'subscriptionId:', subscriptionId)

                      // If we have a saved agency ID, update it directly
                      if (savedAgencyId) {
                        console.log('🔄 Updating existing agency after redirect:', savedAgencyId)

                        // Validate that we have the required price ID
                        if (!savedFormData.selectedPlan.stripePriceId) {
                          console.error('❌ Missing stripePriceId in saved form data:', savedFormData.selectedPlan)
                          throw new Error('Missing price ID - payment processing incomplete')
                        }

                        const updatePayload = {
                          billing: {
                            subscriptions: [{
                              stripeCustomerId: customerId,
                              stripeSubscriptionId: subscriptionId,
                              stripeProducts: [{
                                productType: 'agency_plan' as const,
                                productId: config.stripe.agencyPlanProductId,
                                priceId: savedFormData.selectedPlan.stripePriceId,
                                unitQty: 1,
                                unitPrice: savedFormData.selectedPlan.price,
                                createdAt: new Date().toISOString(),
                                updatedAt: new Date().toISOString()
                              }]
                            }],
                            billingInfo: {
                              companyName: savedFormData.billingInfo.companyName,
                              billingEmail: savedFormData.billingInfo.billingEmail,
                              address: savedFormData.billingInfo.address,
                              city: savedFormData.billingInfo.city,
                              postalCode: savedFormData.billingInfo.postalCode,
                              country: savedFormData.billingInfo.country,
                              vatNumber: savedFormData.billingInfo.vatNumber
                            }
                          }
                        }

                        const updatedAgency = await agencyService.updateAgency(savedAgencyId, updatePayload)
                        console.log('🎉 Agency updated after redirect:', updatedAgency)

                        // Call our payment confirmation API to activate subscription and send welcome email
                        // Check if this payment intent has already been confirmed
                        if (!confirmedPaymentIntents.has(retrievedPaymentIntent.id)) {
                          console.log('📧 Confirming payment and sending activation email after redirect...')
                          try {
                            await paymentService.confirmPayment({
                              paymentIntentId: retrievedPaymentIntent.id,
                              agencyId: savedAgencyId
                            })
                            console.log('✅ Payment confirmed and activation email sent after redirect')
                            // Mark this payment intent as confirmed
                            setConfirmedPaymentIntents(prev => new Set(prev).add(retrievedPaymentIntent.id))
                          } catch (emailError: any) {
                            // Check if this is a "already confirmed" error - if so, treat as success
                            if (emailError?.message?.includes('already succeeded') ||
                              emailError?.error?.includes('already succeeded') ||
                              emailError?.raw?.code === 'payment_intent_unexpected_state') {
                              console.log('✅ Payment was already confirmed, marking as complete')
                              setConfirmedPaymentIntents(prev => new Set(prev).add(retrievedPaymentIntent.id))
                            } else {
                              console.error('⚠️ Failed to send activation email after redirect:', emailError)
                              // Don't fail the whole process for email issues
                            }
                          }
                        } else {
                          console.log('🔄 Payment intent already confirmed, skipping duplicate confirmation')
                        }
                      } else {
                        console.log('⚠️ No saved agency ID found, falling back to comprehensive creation')
                        const updatedAgency = await createAgency({
                          agencyData: savedFormData.agencyData,
                          selectedPlan: savedFormData.selectedPlan,
                          billingInfo: savedFormData.billingInfo,
                          subscriptionId: subscriptionId,
                          customerId: customerId
                        })
                        console.log('🎉 Agency created after redirect:', updatedAgency)

                        // Call our payment confirmation API to activate subscription and send welcome email
                        // Check if this payment intent has already been confirmed
                        if (!confirmedPaymentIntents.has(retrievedPaymentIntent.id)) {
                          console.log('📧 Confirming payment and sending activation email after redirect...')
                          try {
                            await paymentService.confirmPayment({
                              paymentIntentId: retrievedPaymentIntent.id,
                              agencyId: updatedAgency.id
                            })
                            console.log('✅ Payment confirmed and activation email sent after redirect')
                            // Mark this payment intent as confirmed
                            setConfirmedPaymentIntents(prev => new Set(prev).add(retrievedPaymentIntent.id))
                          } catch (emailError: any) {
                            // Check if this is a "already confirmed" error - if so, treat as success
                            if (emailError?.message?.includes('already succeeded') ||
                              emailError?.error?.includes('already succeeded') ||
                              emailError?.raw?.code === 'payment_intent_unexpected_state') {
                              console.log('✅ Payment was already confirmed, marking as complete')
                              setConfirmedPaymentIntents(prev => new Set(prev).add(retrievedPaymentIntent.id))
                            } else {
                              console.error('⚠️ Failed to send activation email after redirect:', emailError)
                              // Don't fail the whole process for email issues
                            }
                          }
                        } else {
                          console.log('🔄 Payment intent already confirmed, skipping duplicate confirmation')
                        }
                      }

                      // Clean up URL parameters after successful update
                      const cleanUrl = new URL(window.location.href)
                      cleanUrl.searchParams.delete('payment_intent')
                      cleanUrl.searchParams.delete('payment_intent_client_secret')
                      cleanUrl.searchParams.delete('data')
                      cleanUrl.searchParams.set('step', '4')
                      navigate(cleanUrl.pathname + cleanUrl.search, { replace: true })
                      setCurrentStep(4)
                      console.log('🧹 Cleaned up URL parameters')
                    } catch (error) {
                      console.error('❌ Error updating agency after redirect:', error)
                      // Clean up URL even on error to prevent retry loops
                      const cleanUrl = new URL(window.location.href)
                      cleanUrl.searchParams.delete('payment_intent')
                      cleanUrl.searchParams.delete('payment_intent_client_secret')
                      cleanUrl.searchParams.delete('data')
                      cleanUrl.searchParams.set('step', '4')
                      navigate(cleanUrl.pathname + cleanUrl.search, { replace: true })
                      setCurrentStep(4)
                    }
                  } else {
                    console.error('❌ Failed to decode form data from URL')
                    setCurrentStep(4)
                  }
                } catch (error) {
                  console.error('❌ Error parsing saved form data from URL:', error)
                  setCurrentStep(4)
                }
              } else {
                console.log('⚠️ No saved form data found in URL')
                setCurrentStep(4)
              }
            } else {
              console.log('⚠️ Payment not successful:', retrievedPaymentIntent?.status)

              // Extract error message from payment intent
              let errorMessage = 'Payment was not successful. Please try again.'
              if (retrievedPaymentIntent?.last_payment_error) {
                const paymentError = retrievedPaymentIntent.last_payment_error
                if (paymentError.message) {
                  errorMessage = paymentError.message
                } else if (paymentError.code) {
                  // Provide user-friendly messages for common error codes
                  switch (paymentError.code) {
                    case 'payment_method_customer_decline':
                      errorMessage = 'Payment was declined or cancelled'
                      break
                    case 'card_declined':
                      errorMessage = 'Your card was declined'
                      break
                    case 'insufficient_funds':
                      errorMessage = 'Insufficient funds'
                      break
                    case 'expired_card':
                      errorMessage = 'Your card has expired'
                      break
                    default:
                      errorMessage = `Payment failed: ${paymentError.code}`
                  }
                }
              }

              setPaymentError(errorMessage)
              // Payment failed, go back to payment step
              setCurrentStep(3)
              setClientSecret(paymentIntentClientSecret)
              setSubscriptionId(paymentIntent)
            }
          }
        } catch (error) {
          console.error('❌ Error checking payment status:', error)
          setCurrentStep(3) // Go back to payment step on error
        } finally {
          setIsCheckingPaymentStatus(false)
        }
      } else if (step === 'complete') {
        console.log('📍 Direct navigation to complete step')
        setCurrentStep(4)
      }
    }

    checkPaymentStatus()
  }, [searchParams])

  const calculateFinalPrice = useCallback(() => {
    return selectedPlan.price
  }, [selectedPlan.price])

  const createAgency = async (formData?: {
    agencyData: AgencyFormData,
    selectedPlan: SubscriptionPlan,
    billingInfo: BillingInfo,
    subscriptionId?: string,
    customerId?: string
  }) => {
    console.log('🚀 createAgency function called')

    // Use provided data or fall back to state
    const dataToUse = formData || {
      agencyData,
      selectedPlan,
      billingInfo,
      subscriptionId: undefined,
      customerId: undefined
    }

    console.log('📝 Agency data:', dataToUse.agencyData)
    console.log('📋 Selected plan:', dataToUse.selectedPlan)
    console.log('💳 Billing info:', dataToUse.billingInfo)

    try {
      // If we have an existing agency ID, update it with payment and billing info
      if (currentAgencyId) {
        console.log('🔄 Updating existing agency with payment and billing info:', currentAgencyId)

        const updatePayload = {
          billing: {
            subscriptions: [{
              stripeCustomerId: dataToUse.customerId,
              stripeSubscriptionId: dataToUse.subscriptionId,
              stripeProducts: [{
                productType: 'agency_plan' as const,
                productId: config.stripe.agencyPlanProductId,
                priceId: dataToUse.selectedPlan.stripePriceId, // This will now be the dynamic price ID
                unitQty: 1,
                unitPrice: dataToUse.selectedPlan.price,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              }]
            }],
            billingInfo: {
              companyName: dataToUse.billingInfo.companyName,
              billingEmail: dataToUse.billingInfo.billingEmail,
              address: dataToUse.billingInfo.address,
              city: dataToUse.billingInfo.city,
              postalCode: dataToUse.billingInfo.postalCode,
              country: dataToUse.billingInfo.country,
              vatNumber: dataToUse.billingInfo.vatNumber
            }
          }
        }

        console.log('📤 Agency update payload:', JSON.stringify(updatePayload, null, 2))

        const updatedAgency = await agencyService.updateAgency(currentAgencyId, updatePayload)
        console.log('✅ Agency updated successfully:', updatedAgency)
        return updatedAgency
      } else {
        // Fallback: create comprehensive agency (for backward compatibility)
        // Authentication is handled automatically

        const agencyPayload = {
          name: dataToUse.agencyData.name,
          agencyCode: dataToUse.agencyData.agencyCode,
          phone: dataToUse.agencyData.phone,
          description: dataToUse.agencyData.description,
          website: dataToUse.agencyData.website,

          // createdBy will be handled by the API based on JWT token
          billing: {
            subscriptions: [{
              stripeCustomerId: dataToUse.customerId,
              stripeSubscriptionId: dataToUse.subscriptionId,
              stripeProducts: [{
                productType: 'agency_plan' as const,
                productId: config.stripe.agencyPlanProductId,
                priceId: dataToUse.selectedPlan.stripePriceId, // This will now be the dynamic price ID
                unitQty: 1,
                unitPrice: dataToUse.selectedPlan.price,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              }]
            }],
            billingInfo: {
              companyName: dataToUse.billingInfo.companyName,
              billingEmail: dataToUse.billingInfo.billingEmail,
              address: dataToUse.billingInfo.address,
              city: dataToUse.billingInfo.city,
              postalCode: dataToUse.billingInfo.postalCode,
              country: dataToUse.billingInfo.country,
              vatNumber: dataToUse.billingInfo.vatNumber
            }
          }
        }

        console.log('📤 Sending comprehensive agency payload:', JSON.stringify(agencyPayload, null, 2))
        const createdAgency = await agencyService.createAgencyComprehensive(agencyPayload)
        console.log('✅ Agency created successfully:', createdAgency)
        setCreatedAgencyId(createdAgency.id)
        return createdAgency
      }
    } catch (error) {
      console.error('❌ Error creating/updating agency:', error)
      throw error
    }
  }

  const handleNext = async () => {
    // Step 1 - Validate and create agency (only if not existing agency)
    if (currentStep === 1 && !isExistingAgency) {
      // Validation
      if (!agencyData.name.trim() || !agencyData.agencyCode.trim() || !agencyData.phone.trim() || !agencyData.website.trim()) {
        alert('Please fill in all required fields (Agency Name, Agency Code, Phone Number, and Website)')
        return
      }
      if (agencyData.name.trim().length < 2) {
        alert('Agency name must be at least 2 characters long')
        return
      }
      if (agencyData.agencyCode.trim().length < 2) {
        alert('Agency code must be at least 2 characters long')
        return
      }
      if (!/^[a-z0-9-]+$/.test(agencyData.agencyCode)) {
        alert('Agency code can only contain lowercase letters, numbers, and hyphens')
        return
      }
      if (agencyData.website.trim() && !/^https?:\/\/.+/.test(agencyData.website.trim())) {
        alert('Website must be a valid URL starting with http:// or https://')
        return
      }

      // Create basic agency
      try {
        setIsLoading(true)
        console.log('🚀 Creating basic agency in step 1...')

        // Authentication is handled automatically
        const createdAgency = await agencyService.createBasicAgency({
          name: agencyData.name,
          agencyCode: agencyData.agencyCode,
          phone: agencyData.phone,
          description: agencyData.description,
          website: agencyData.website,

          // createdBy will be handled by the API based on JWT token
        })

        console.log('✅ Basic agency created:', createdAgency)
        setCurrentAgencyId(createdAgency.id)
        setCreatedAgencyId(createdAgency.id)

        // Move to next step and update URL
        setCurrentStep(2)
        navigate(`/agencies/create?step=2&agency=${createdAgency.id}`, { replace: true })
      } catch (error) {
        console.error('❌ Error creating basic agency:', error)
        alert('Failed to create agency. Please try again.')
      } finally {
        setIsLoading(false)
      }
      return
    }

    // Step 2 - Move to payment step and update URL
    if (currentStep === 2) {
      setCurrentStep(3)
      const agencyId = currentAgencyId || existingAgencyId
      navigate(`/agencies/create?step=3&agency=${agencyId}`, { replace: true })
      return
    }

    // Step 3 - Save billing info and process payment (handled in PaymentInformationStep)
    if (currentStep === 3) {
      // This step is handled by the PaymentInformationStep component
      // We don't need to do anything here as the payment form handles the submission
      return
    }

    // Default case - just move to next step
    setCurrentStep(currentStep + 1)
  }

  const handleBack = () => {
    if (currentStep > 1) {
      const newStep = currentStep - 1
      setCurrentStep(newStep)

      if (newStep === 1 && !isExistingAgency) {
        navigate('/agencies/create?step=1', { replace: true })
      } else if (newStep === 2) {
        const agencyId = currentAgencyId || existingAgencyId
        navigate(`/agencies/create?step=2&agency=${agencyId}`, { replace: true })
      } else if (newStep === 1 && isExistingAgency) {
        // If existing agency, step 1 should redirect to agencies list
        navigate('/agencies')
      }
    } else {
      navigate('/agencies')
    }
  }

  const steps = [
    { number: 1, title: 'Agency Information', description: 'Basic agency details' },
    { number: 2, title: 'Subscription Plan', description: 'Choose your plan' },
    { number: 3, title: 'Payment Information', description: 'Complete your payment' },
    { number: 4, title: 'Complete', description: 'Setup complete' }
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="relative">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="flex items-center text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-4"
          >
            <BiArrowToLeft size={20} className="mr-2" />
            Back
          </button>

          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
            {isExistingAgency ? 'Setup Subscription' : 'Create New Agency'}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">
            {isExistingAgency ? 'Complete your agency subscription setup' : 'Set up your agency account in just a few steps'}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${currentStep > step.number || (currentStep === step.number && step.number === 4)
                      ? 'bg-green-500 text-white'
                      : currentStep === step.number
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                      }`}
                  >
                    {currentStep > step.number || (currentStep === step.number && step.number === 4) ? (
                      <BiCheck size={20} />
                    ) : (
                      step.number
                    )}
                  </div>
                  <div className="mt-2 text-center">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      {step.title}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {step.description}
                    </p>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700 mx-4 mt-5" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6 relative">
          {/* Payment Status Check Loading Overlay */}
          {isCheckingPaymentStatus && (
            <div className="absolute inset-0 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
              <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-lg border border-slate-200 dark:border-slate-600">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <div>
                    <p className="text-lg font-semibold text-slate-900 dark:text-white">Processing Payment</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Verifying your payment and creating your agency...
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && !isExistingAgency && <AgencyInformationStep data={agencyData} onChange={setAgencyData} />}
          {currentStep === 2 && (
            <SubscriptionPlanStep
              selectedPlan={selectedPlan}
              onPlanSelect={setSelectedPlan}
              finalPrice={calculateFinalPrice()}
            />
          )}
          {currentStep === 3 && (
            <PaymentInformationStep
              billingInfo={billingInfo}
              onBillingInfoChange={setBillingInfo}
              subscriptionId={subscriptionId}
              onPaymentComplete={createAgency}
              onStepChange={setCurrentStep}
              finalPrice={calculateFinalPrice()}
              planName={selectedPlan.name}
              agencyData={agencyData}
              selectedPlan={selectedPlan}
              paymentError={paymentError}
              onClearPaymentError={() => setPaymentError('')}
              currentAgencyId={currentAgencyId}
              onSelectedPlanChange={setSelectedPlan}
              confirmedPaymentIntents={confirmedPaymentIntents}
              onPaymentIntentConfirmed={(paymentIntentId: string) =>
                setConfirmedPaymentIntents(prev => new Set(prev).add(paymentIntentId))
              }
            />
          )}
          {currentStep === 4 && (
            <CompleteStep
              agencyData={agencyData}
              selectedPlan={selectedPlan}
              finalPrice={calculateFinalPrice()}
              createdAgencyId={createdAgencyId}
            />
          )}
        </div>

        {/* Navigation */}
        {currentStep < 4 && (
          <div className="mt-6 flex justify-between">
            <button
              onClick={handleBack}
              className="px-6 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              {(currentStep === 1 && !isExistingAgency) || (currentStep === 2 && isExistingAgency) ? 'Cancel' : 'Back'}
            </button>

            <button
              onClick={handleNext}
              disabled={
                isLoading ||
                (currentStep === 3) ||
                (currentStep === 1 && !isExistingAgency && (!agencyData.name.trim() || !agencyData.agencyCode.trim() || agencyData.name.length < 2 || agencyData.agencyCode.length < 2 || !/^[a-z0-9-]+$/.test(agencyData.agencyCode)))
              }
              className={`px-6 py-2 rounded-lg font-medium transition-all flex items-center ${isLoading ||
                (currentStep === 3) ||
                (currentStep === 1 && !isExistingAgency && (!agencyData.name.trim() || !agencyData.agencyCode.trim() || agencyData.name.length < 2 || agencyData.agencyCode.length < 2 || !/^[a-z0-9-]+$/.test(agencyData.agencyCode)))
                ? 'bg-slate-300 dark:bg-slate-600 text-slate-500 dark:text-slate-400 cursor-not-allowed opacity-60'
                : 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg'
                }`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : currentStep === 3 ? (
                'Complete Payment Below'
              ) : (
                <>
                  Next
                  <BiChevronRight size={20} className="ml-1" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// Step Components
const AgencyInformationStep: React.FC<{
  data: AgencyFormData
  onChange: (data: AgencyFormData) => void
}> = ({ data, onChange }) => {
  const [isCheckingCode, setIsCheckingCode] = useState(false)
  const [codeAvailable, setCodeAvailable] = useState<boolean | null>(null)
  const [codeError, setCodeError] = useState<string | null>(null)
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set())

  // Debounced function to check agency code availability
  const checkAgencyCodeAvailability = useCallback(
    debounce(async (agencyCode: string) => {
      if (!agencyCode || agencyCode.length < 2) {
        setCodeAvailable(null)
        setCodeError(null)
        return
      }

      if (!/^[a-z0-9-]+$/.test(agencyCode)) {
        setCodeAvailable(false)
        setCodeError('Agency code can only contain lowercase letters, numbers, and hyphens')
        return
      }

      setIsCheckingCode(true)
      setCodeError(null)

      try {
        const response = await agencyService.checkAgencyCodeAvailability(agencyCode)
        setCodeAvailable(response.available)
        if (!response.available) {
          setCodeError('This agency code is already taken')
        }
      } catch (error: any) {
        setCodeAvailable(false)
        setCodeError(error?.error || error?.message || 'Error checking availability')
      } finally {
        setIsCheckingCode(false)
      }
    }, 1000),
    []
  )

  const handleChange = (field: keyof AgencyFormData, value: string) => {
    // Mark field as touched
    setTouchedFields(prev => new Set(prev).add(field))

    onChange({ ...data, [field]: value })

    // Auto-generate agency code from name
    if (field === 'name') {
      const generatedCode = value
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .substring(0, 50)
      onChange({ ...data, [field]: value, agencyCode: generatedCode })

      // Check availability of generated code
      if (generatedCode) {
        checkAgencyCodeAvailability(generatedCode)
      }
    }

    // Check availability when agency code is manually changed
    if (field === 'agencyCode') {
      const cleanedCode = value
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
      onChange({ ...data, [field]: cleanedCode })
      checkAgencyCodeAvailability(cleanedCode)
    }
  }

  // Debounce utility function
  function debounce(func: Function, wait: number) {
    let timeout: number
    return function executedFunction(...args: any[]) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-6">
        Agency Information
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Agency Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter agency name"
            minLength={2}
            required
          />
          {data.name.length > 0 && data.name.length < 2 && (
            <p className="text-red-500 text-sm mt-1">Agency name must be at least 2 characters long</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Agency Code <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="text"
              value={data.agencyCode}
              onChange={(e) => handleChange('agencyCode', e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter agency code"
              required
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {isCheckingCode && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              )}
              {!isCheckingCode && codeAvailable === true && (
                <BiCheck className="text-green-500" size={20} />
              )}
              {!isCheckingCode && codeAvailable === false && (
                <div className="text-red-500">✕</div>
              )}
            </div>
          </div>
          {data.agencyCode.length > 0 && data.agencyCode.length < 2 && (
            <p className="text-red-500 text-sm mt-1">Agency code must be at least 2 characters long</p>
          )}
          {data.agencyCode.length > 0 && !/^[a-z0-9-]+$/.test(data.agencyCode) && (
            <p className="text-red-500 text-sm mt-1">Agency code can only contain lowercase letters, numbers, and hyphens</p>
          )}
          {codeError && (
            <p className="text-red-500 text-sm mt-1">{codeError}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={data.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            onBlur={() => setTouchedFields(prev => new Set(prev).add('phone'))}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Enter phone number"
            required
          />
          {touchedFields.has('phone') && data.phone.length === 0 && (
            <p className="text-red-500 text-sm mt-1">Phone number is required</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Website <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            value={data.website}
            onChange={(e) => handleChange('website', e.target.value)}
            onBlur={() => setTouchedFields(prev => new Set(prev).add('website'))}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="https://yourwebsite.com"
            required
          />
          {touchedFields.has('website') && data.website.length === 0 && (
            <p className="text-red-500 text-sm mt-1">Website is required</p>
          )}
          {data.website.length > 0 && !/^https?:\/\/.+/.test(data.website) && (
            <p className="text-amber-600 text-sm mt-1">Website should start with http:// or https://</p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Description
          </label>
          <textarea
            value={data.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Describe your agency, services, or specializations"
          />
        </div>


      </div>
    </div>
  )
}

const SubscriptionPlanStep: React.FC<{
  selectedPlan: SubscriptionPlan
  onPlanSelect: (plan: SubscriptionPlan) => void
  finalPrice: number
}> = ({
  selectedPlan,
  onPlanSelect,
  finalPrice
}) => {
    return (
      <div>
        <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-6">
          Choose Your Plan
        </h2>

        <div className="mb-8">
          {[selectedPlan].map((plan) => (
            <div
              key={plan.id}
              className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${selectedPlan.id === plan.id
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-slate-200 dark:border-slate-600 hover:border-blue-300'
                }`}
              onClick={() => onPlanSelect(plan)}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white">
                    {plan.name}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 mt-1">
                    {plan.description}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">
                    £{plan.price.toFixed(2)}
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    per month
                  </div>
                </div>
              </div>

              <ul className="space-y-2">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-slate-700 dark:text-slate-300">
                    <BiCheck className="text-green-500 mr-2" size={16} />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Price Summary */}
        <div className="mt-6 bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <span className="text-slate-600 dark:text-slate-400">
              {selectedPlan.name}
            </span>
            <span className="text-slate-900 dark:text-white">
              £{selectedPlan.price.toFixed(2)}
            </span>
          </div>

          <div className="border-t border-slate-200 dark:border-slate-600 mt-3 pt-3">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-slate-900 dark:text-white">
                Total
              </span>
              <span className="text-lg font-semibold text-slate-900 dark:text-white">
                £{finalPrice.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    )
  }

const PaymentInformationStep: React.FC<{
  billingInfo: BillingInfo
  onBillingInfoChange: (info: BillingInfo) => void
  subscriptionId: string
  onPaymentComplete: (formData?: {
    agencyData: AgencyFormData,
    selectedPlan: SubscriptionPlan,
    billingInfo: BillingInfo,
    subscriptionId?: string,
    customerId?: string
  }) => Promise<any>
  onStepChange: (step: number) => void
  finalPrice: number
  planName: string
  agencyData: AgencyFormData
  selectedPlan: SubscriptionPlan
  paymentError?: string
  onClearPaymentError?: () => void
  currentAgencyId: string
  onSelectedPlanChange: (plan: SubscriptionPlan) => void
  confirmedPaymentIntents: Set<string>
  onPaymentIntentConfirmed: (paymentIntentId: string) => void
}> = ({ billingInfo, onBillingInfoChange, subscriptionId, onPaymentComplete, onStepChange, finalPrice, planName, agencyData, selectedPlan, paymentError, onClearPaymentError, currentAgencyId, onSelectedPlanChange, confirmedPaymentIntents, onPaymentIntentConfirmed }) => {
  console.log('🏗️ PaymentInformationStep rendered with props:', {
    subscriptionId,
    finalPrice,
    planName,
    onPaymentComplete: typeof onPaymentComplete,
    onStepChange: typeof onStepChange
  })

  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [clientSecret, setClientSecret] = useState<string>('')
  const [subscriptionCreated, setSubscriptionCreated] = useState(false)
  const [emailValidationError, setEmailValidationError] = useState('')

  const handleBillingChange = (field: keyof BillingInfo, value: string) => {
    // Don't allow changes while processing
    if (isProcessing) return

    // Real-time email validation
    if (field === 'billingEmail') {
      if (value.trim() === '') {
        setEmailValidationError('')
      } else if (!validateEmail(value)) {
        setEmailValidationError('Please enter a valid email address with a proper domain (e.g., name@company.com)')
      } else {
        setEmailValidationError('')
      }
    }

    onBillingInfoChange({ ...billingInfo, [field]: value })
  }

  // Email validation function to match Stripe's requirements
  const validateEmail = (email: string): boolean => {
    // Stripe requires a complete email format with a valid TLD
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/
    return emailRegex.test(email.trim())
  }

  const createSubscriptionAndSetupPayment = async () => {
    // Validate billing info first
    if (!billingInfo.companyName.trim() || !billingInfo.billingEmail.trim() || !billingInfo.address.trim() || !billingInfo.city.trim() || !billingInfo.postalCode.trim()) {
      setErrorMessage('Please fill in all required billing information fields.')
      return
    }

    // Validate email format specifically for Stripe compatibility
    if (!validateEmail(billingInfo.billingEmail)) {
      setErrorMessage('Please enter a valid email address (e.g., name@company.com). Emails without a proper domain extension are not accepted.')
      return
    }

    setIsProcessing(true)
    setErrorMessage('')
    setEmailValidationError('')

    try {
      // Create the subscription with the billing information
      console.log('🔄 Creating subscription with billing information...')

      const cleanPrice = Math.round(selectedPlan.price * 100) / 100
      console.log('💰 Price debugging:', {
        'selectedPlan.price': selectedPlan.price,
        'cleanPrice': cleanPrice,
        'selectedPlan.price type': typeof selectedPlan.price,
        'cleanPrice type': typeof cleanPrice
      })

      const subscriptionResponse = await paymentService.createSubscription({
        customPrice: cleanPrice, // Ensure proper rounding to 2 decimal places
        agencyId: currentAgencyId,
        customerData: {
          name: billingInfo.companyName,
          email: billingInfo.billingEmail,
          phone: agencyData.phone,
          address: {
            line1: billingInfo.address,
            city: billingInfo.city,
            postal_code: billingInfo.postalCode,
            country: billingInfo.country,
          },
        }
      })

      if (!subscriptionResponse.success || !subscriptionResponse.data) {
        setErrorMessage('Failed to create subscription. Please try again.')
        return
      }

      console.log('✅ Subscription created:', subscriptionResponse.data)
      console.log('🔍 Full response structure:', JSON.stringify(subscriptionResponse.data, null, 2))

      // Extract data from the response
      const responseData = subscriptionResponse.data
      const newClientSecret = responseData.clientSecret
      const newSubscriptionId = responseData.subscriptionId
      const newCustomerId = responseData.customerId
      const requiresPayment = responseData.requiresPayment
      const dynamicPriceId = responseData.priceId // Get the dynamic price ID

      console.log('📋 Extracted data:', {
        newClientSecret: !!newClientSecret,
        newSubscriptionId,
        newCustomerId,
        requiresPayment,
        dynamicPriceId
      })

      // Validate required data
      if (!newSubscriptionId || !newCustomerId) {
        console.error('❌ Missing required subscription data:', { newSubscriptionId, newCustomerId })
        setErrorMessage('Failed to create subscription. Missing required data.')
        return
      }

      // If payment is not required (100% discount), complete the agency setup immediately
      if (!requiresPayment) {
        console.log('🎉 No payment required! Completing agency setup...')
        try {
          await onPaymentComplete({
            agencyData,
            selectedPlan: {
              ...selectedPlan,
              stripePriceId: dynamicPriceId || selectedPlan.stripePriceId // Use dynamic price ID if available
            },
            billingInfo,
            subscriptionId: newSubscriptionId,
            customerId: newCustomerId
          })
          console.log('✅ Agency setup completed without payment')
          onStepChange(4) // Move to completion step
          return
        } catch (error) {
          console.error('❌ Error completing agency setup:', error)
          setErrorMessage('Failed to complete agency setup. Please contact support.')
          return
        }
      }

      // For regular payments, validate client secret
      if (!newClientSecret) {
        console.error('❌ Missing client secret for payment:', { newClientSecret: !!newClientSecret })
        setErrorMessage('Failed to create subscription. Missing payment data.')
        return
      }

      // Save subscription data for payment processing
      setClientSecret(newClientSecret)

      // Calculate final price ID  
      const finalPriceId = dynamicPriceId || selectedPlan.stripePriceId
      console.log('🔍 Price ID debugging:', {
        dynamicPriceId,
        'selectedPlan.stripePriceId': selectedPlan.stripePriceId,
        finalPriceId
      })

      // Update the selected plan state with the dynamic price ID
      if (finalPriceId) {
        const updatedPlan = {
          ...selectedPlan,
          stripePriceId: finalPriceId
        }
        onSelectedPlanChange(updatedPlan)
        console.log('📋 Updated selected plan with dynamic price ID:', updatedPlan)
      }

      // Store form data in URL parameters for payment processing
      const formDataToSave: FormDataForRedirect = {
        agencyData,
        selectedPlan: {
          ...selectedPlan,
          stripePriceId: finalPriceId // Use dynamic price ID if available
        },
        billingInfo,
        subscriptionId: newSubscriptionId,
        customerId: newCustomerId
      }
      const encodedFormData = encodeFormData(formDataToSave)

      // Update URL with form data
      const newUrl = new URL(window.location.href)
      newUrl.searchParams.set('data', encodedFormData)
      window.history.replaceState({}, '', newUrl.toString())

      console.log('💾 Form data stored in URL parameters for payment processing')
      console.log('🔍 Form data being saved:', formDataToSave)
      console.log('🔍 Encoded data preview:', encodedFormData.substring(0, 50) + '...')

      setSubscriptionCreated(true)
    } catch (error) {
      console.error('❌ Error creating subscription:', error)
      setErrorMessage('Failed to create subscription. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  if (!subscriptionCreated) {
    return (
      <div className="relative">
        {/* Loading Overlay */}
        {isProcessing && (
          <div className="absolute inset-0 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-lg border border-slate-200 dark:border-slate-600">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <div>
                  <p className="text-lg font-semibold text-slate-900 dark:text-white">Creating Subscription</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Please wait while we set up your subscription...</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-6">
          Payment Information
        </h2>

        {/* Order Summary */}
        <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Order Summary</h3>
          <div className="flex justify-between items-center">
            <span className="text-slate-600 dark:text-slate-400">{planName}</span>
            <span className="font-semibold text-slate-900 dark:text-white">£{finalPrice.toFixed(2)}</span>
          </div>
        </div>

        {/* Payment Error Display */}
        {paymentError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="text-red-600 dark:text-red-400 mr-3">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-red-800 dark:text-red-200 font-medium">Setup Failed</h4>
                  <p className="text-red-700 dark:text-red-300 text-sm">{paymentError}</p>
                </div>
              </div>
              {onClearPaymentError && (
                <button
                  onClick={onClearPaymentError}
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Billing Information */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              Billing Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={billingInfo.companyName}
                  onChange={(e) => handleBillingChange('companyName', e.target.value)}
                  disabled={isProcessing}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Billing Email *
                </label>
                <input
                  type="email"
                  value={billingInfo.billingEmail}
                  onChange={(e) => handleBillingChange('billingEmail', e.target.value)}
                  disabled={isProcessing}
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${emailValidationError
                    ? 'border-red-300 dark:border-red-600 focus:ring-red-500'
                    : 'border-slate-300 dark:border-slate-600 focus:ring-primary-500'
                    }`}
                  required
                />
                {emailValidationError && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1">{emailValidationError}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Address *
                </label>
                <input
                  type="text"
                  value={billingInfo.address}
                  onChange={(e) => handleBillingChange('address', e.target.value)}
                  disabled={isProcessing}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  value={billingInfo.city}
                  onChange={(e) => handleBillingChange('city', e.target.value)}
                  disabled={isProcessing}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Postal Code *
                </label>
                <input
                  type="text"
                  value={billingInfo.postalCode}
                  onChange={(e) => handleBillingChange('postalCode', e.target.value)}
                  disabled={isProcessing}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Country *
                </label>
                <select
                  value={billingInfo.country}
                  onChange={(e) => handleBillingChange('country', e.target.value)}
                  disabled={isProcessing}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                >
                  <option value="GB">United Kingdom</option>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="AU">Australia</option>
                  <option value="DE">Germany</option>
                  <option value="FR">France</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  VAT Number (Optional)
                </label>
                <input
                  type="text"
                  value={billingInfo.vatNumber}
                  onChange={(e) => handleBillingChange('vatNumber', e.target.value)}
                  disabled={isProcessing}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {errorMessage && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-700 dark:text-red-400">{errorMessage}</p>
            </div>
          )}

          <button
            onClick={createSubscriptionAndSetupPayment}
            disabled={isProcessing}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Setting up subscription...
              </>
            ) : (
              `Continue to Payment (£${finalPrice.toFixed(2)})`
            )}
          </button>
        </div>
      </div>
    )
  }

  // Render the Stripe Elements payment form
  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'night',
          labels: 'floating',
          variables: {
            fontFamily: 'Sohne, system-ui, sans-serif',
            fontWeightNormal: '500',
            borderRadius: '8px',
            colorBackground: '#0A2540',
            colorPrimary: '#EFC078',
            accessibleColorOnColorPrimary: '#1A1B25',
            colorText: 'white',
            colorTextSecondary: 'white',
            colorTextPlaceholder: '#ABB2BF',
            tabIconColor: 'white',
            logoColor: 'dark'
          },
          rules: {
            '.Input': {
              backgroundColor: '#212D63',
              border: '1px solid var(--colorBackground)'
            },
            '.Input:focus': {
              backgroundColor: '#212D63',
              border: '1px solid var(--colorBackground)'
            }
          }
        }
      }}
    >
      <StripePaymentForm
        finalPrice={finalPrice}
        agencyData={agencyData}
        selectedPlan={selectedPlan}
        billingInfo={billingInfo}
        onPaymentComplete={onPaymentComplete}
        onStepChange={onStepChange}
        currentAgencyId={currentAgencyId}
        confirmedPaymentIntents={confirmedPaymentIntents}
        onPaymentIntentConfirmed={(paymentIntentId: string) =>
          onPaymentIntentConfirmed(paymentIntentId)
        }
      />
    </Elements>
  )
}

const CompleteStep: React.FC<{
  agencyData: AgencyFormData
  selectedPlan: SubscriptionPlan
  finalPrice: number
  createdAgencyId: string
}> = ({ agencyData, selectedPlan, finalPrice, createdAgencyId }) => {
  const navigate = useNavigate()

  // Auto-redirect to agency page when component mounts
  useEffect(() => {
    // Removed automatic redirect - user will manually navigate using the button
  }, [createdAgencyId, navigate])

  return (
    <div className="text-center">
      <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
        <BiCheck className="text-green-500" size={32} />
      </div>

      <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">
        Setup Complete!
      </h2>

      <p className="text-slate-600 dark:text-slate-400 mb-6">
        Your agency "{agencyData.name}" has been set up successfully and your subscription is now active.
      </p>

      <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Summary</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-600 dark:text-slate-400">Agency Name:</span>
            <span className="text-slate-900 dark:text-white">{agencyData.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-600 dark:text-slate-400">Plan:</span>
            <span className="text-slate-900 dark:text-white">{selectedPlan.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-600 dark:text-slate-400">Monthly Cost:</span>
            <span className="text-slate-900 dark:text-white">£{finalPrice.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="text-sm text-slate-500 dark:text-slate-400 mb-4">
        Your agency is now set up and ready to use.
      </div>

      <button
        onClick={() => {
          const agencyId = createdAgencyId || (new URLSearchParams(window.location.search).get('agency'))
          navigate(agencyId ? `/agencies/${agencyId}` : '/agencies')
        }}
        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
      >
        Go to Agency Dashboard Now
      </button>
    </div>
  )
}

// Stripe Payment Form Component (must be inside Elements provider)
const StripePaymentForm: React.FC<{
  finalPrice: number
  agencyData: AgencyFormData
  selectedPlan: SubscriptionPlan
  billingInfo: BillingInfo
  onPaymentComplete: (formData?: {
    agencyData: AgencyFormData,
    selectedPlan: SubscriptionPlan,
    billingInfo: BillingInfo,
    subscriptionId?: string,
    customerId?: string
  }) => Promise<any>
  onStepChange: (step: number) => void
  currentAgencyId: string
  confirmedPaymentIntents: Set<string>
  onPaymentIntentConfirmed: (paymentIntentId: string) => void
}> = ({ finalPrice, agencyData, selectedPlan, billingInfo, onPaymentComplete, onStepChange, currentAgencyId, confirmedPaymentIntents, onPaymentIntentConfirmed }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handlePayment = async (e: React.FormEvent) => {
    console.log('💳 handlePayment called')
    e.preventDefault()

    if (!stripe || !elements) {
      console.log('❌ Stripe or elements not available')
      return
    }

    setIsProcessing(true)
    setErrorMessage('')

    try {
      // Get saved subscription data from URL parameters
      const urlParams = new URLSearchParams(window.location.search)
      const encodedData = urlParams.get('data')
      let subscriptionId = ''
      let customerId = ''

      if (encodedData) {
        console.log('🔍 Found encoded data in URL:', encodedData.substring(0, 50) + '...')
        const savedFormData = decodeFormData(encodedData)
        console.log('🔍 Decoded form data:', savedFormData)

        if (savedFormData && savedFormData.subscriptionId && savedFormData.customerId) {
          subscriptionId = savedFormData.subscriptionId
          customerId = savedFormData.customerId
          console.log('📋 Retrieved from URL parameters - subscriptionId:', subscriptionId, 'customerId:', customerId)
        } else {
          console.error('❌ Invalid form data in URL - missing required fields')
          console.log('❌ Saved form data:', savedFormData)
          setErrorMessage('Payment session expired. Please try again.')
          return
        }
      } else {
        console.error('❌ No saved form data found in URL')
        setErrorMessage('Payment session expired. Please try again.')
        return
      }

      console.log('🔄 Confirming payment with Stripe...')

      // Encode form data for the return URL
      const formDataForRedirect: FormDataForRedirect = {
        agencyData,
        selectedPlan,
        billingInfo,
        subscriptionId,
        customerId
      }
      const encodedFormData = encodeFormData(formDataForRedirect)

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/agencies/create?step=complete&data=${encodeURIComponent(encodedFormData)}${currentAgencyId ? `&agency=${currentAgencyId}` : ''}`,
          payment_method_data: {
            billing_details: {
              name: billingInfo.companyName,
              email: billingInfo.billingEmail,
              address: {
                line1: billingInfo.address,
                city: billingInfo.city,
                postal_code: billingInfo.postalCode,
                country: billingInfo.country,
              },
            },
          },
        },
        redirect: 'if_required',
      })

      console.log('📋 Payment result:', { error, paymentIntent })

      if (error) {
        console.log('❌ Payment error:', error)
        setErrorMessage(error.message || 'Payment failed')
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log('✅ Payment succeeded without redirect! Updating agency with subscription info...')
        // Payment successful - now update the agency with subscription and billing info
        try {
          const updatedAgency = await onPaymentComplete({
            agencyData,
            selectedPlan,
            billingInfo,
            subscriptionId: subscriptionId,
            customerId: customerId
          })
          console.log('🎉 Agency update completed:', updatedAgency)

          // Call our payment confirmation API to activate subscription and send welcome email
          // Check if this payment intent has already been confirmed
          if (!confirmedPaymentIntents.has(paymentIntent.id)) {
            console.log('📧 Confirming payment and sending activation email...')
            try {
              await paymentService.confirmPayment({
                paymentIntentId: paymentIntent.id,
                agencyId: updatedAgency.id
              })
              console.log('✅ Payment confirmed and activation email sent')
              // Mark this payment intent as confirmed
              onPaymentIntentConfirmed(paymentIntent.id)
            } catch (emailError: any) {
              // Check if this is a "already confirmed" error - if so, treat as success
              if (emailError?.message?.includes('already succeeded') ||
                emailError?.error?.includes('already succeeded') ||
                emailError?.raw?.code === 'payment_intent_unexpected_state') {
                console.log('✅ Payment was already confirmed, marking as complete')
                onPaymentIntentConfirmed(paymentIntent.id)
              } else {
                console.error('⚠️ Failed to send activation email:', emailError)
                // Don't fail the whole process for email issues
              }
            }
          } else {
            console.log('🔄 Payment intent already confirmed, skipping duplicate confirmation')
          }

          // Move to completion step
          onStepChange(4)
        } catch (error) {
          console.error('❌ Error updating agency after payment:', error)
          setErrorMessage('Payment successful but failed to update agency. Please contact support.')
        }
      } else {
        console.log('⚠️ Payment status not succeeded:', paymentIntent?.status)
        if (paymentIntent?.status === 'requires_action') {
          console.log('🔄 Payment requires additional action - likely redirecting to Stripe...')
        }
      }
    } catch (error) {
      console.error('❌ Payment error:', error)
      setErrorMessage('Failed to process payment. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className="relative">
      {/* Loading Overlay */}
      {isProcessing && (
        <div className="absolute inset-0 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-lg border border-slate-200 dark:border-slate-600">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <div>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">Processing Payment</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">Please wait while we process your payment...</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-6">
        Complete Your Payment
      </h2>

      {/* Order Summary */}
      <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Order Summary</h3>
        <div className="flex justify-between items-center">
          <span className="text-slate-600 dark:text-slate-400">{selectedPlan.name}</span>
          <span className="font-semibold text-slate-900 dark:text-white">£{finalPrice.toFixed(2)}</span>
        </div>
      </div>

      <form onSubmit={handlePayment} className="space-y-6">
        {/* Payment Element */}
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Payment Method
          </h3>
          <div className={`border border-slate-300 dark:border-slate-600 rounded-lg p-4 bg-white dark:bg-slate-700 ${isProcessing ? 'opacity-50 pointer-events-none' : ''}`}>
            <PaymentElement />
          </div>
        </div>

        {errorMessage && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-700 dark:text-red-400">{errorMessage}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
        >
          {isProcessing ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Processing Payment...
            </>
          ) : (
            `Pay £${finalPrice.toFixed(2)}`
          )}
        </button>
      </form>
    </div>
  )
}

export default CreateAgencyWizard